import { Fragment } from 'react';
import { t } from 'i18next';
import { Link } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Dashboard,
  ManageAccounts,
  DriveFileMove,
  Assignment,
  Group,
  Download,
  PendingActions,
  BarChart,
  BeachAccess,
} from '@mui/icons-material';
import { useSidebar } from './hooks';
import { theme } from '@store';

export type SidebarPaths =
  | '/dashboard'
  | '/worklist'
  | '/patient'
  | '/reporting'
  | '/downloads'
  | '/admin'
  | '/activity-logs'
  | '/time-off'
  | '/data-analysis';

export interface SidebarOption {
  label: string;
  path: SidebarPaths;
  icon: React.ReactElement;
  hasDivider?: boolean;
  requiredPermissions?: string[];
}

const sidebarOptions: SidebarOption[] = [
  {
    label: 'sidebar.dashboard',
    path: '/dashboard',
    icon: (
      <Dashboard sx={{ width: '1.5rem', height: 'auto' }} color='primary' />
    ),
  },
  {
    label: 'sidebar.worklist',
    path: '/worklist',
    icon: (
      <Assignment sx={{ width: '1.5rem', height: 'auto' }} color='primary' />
    ),
  },
  {
    label: 'sidebar.patients',
    path: '/patient',
    icon: <Group sx={{ width: '1.5rem', height: 'auto' }} color='primary' />,
  },
  {
    label: 'sidebar.dataAnalysis',
    path: '/data-analysis',
    icon: (
      <DriveFileMove sx={{ width: '1.5rem', height: 'auto' }} color='primary' />
    ),
    requiredPermissions: ['manage_submissions'],
  },
  {
    label: 'sidebar.activityLogs',
    path: '/activity-logs',
    icon: (
      <PendingActions
        sx={{ width: '1.5rem', height: 'auto' }}
        color='primary'
      />
    ),
    hasDivider: false,
  },

  {
    label: 'sidebar.reporting',
    path: '/reporting',
    icon: <BarChart sx={{ width: '1.5rem', height: 'auto' }} color='primary' />,
    requiredPermissions: ['view_reports'],
  },
  {
    label: 'sidebar.downloads',
    path: '/downloads',
    icon: <Download sx={{ width: '1.5rem', height: 'auto' }} color='primary' />,
    hasDivider: true,
    requiredPermissions: ['view_reports'],
  },
  {
    label: 'sidebar.admin',
    path: '/admin',
    icon: (
      <ManageAccounts
        sx={{ width: '1.5rem', height: 'auto' }}
        color='primary'
      />
    ),
    hasDivider: true,
  },
  {
    label: 'sidebar.timeOff',
    path: '/time-off',
    icon: (
      <BeachAccess sx={{ width: '1.5rem', height: 'auto' }} color='primary' />
    ),
    hasDivider: false,
    requiredPermissions: ['view_time_off', 'manage_time_off'],
  },
];

export const Sidebar = () => {
  const {
    isOptionFocused,
    isSidebarOpen,
    handleDrawerToggle,
    hasAdminPanelPermission,
    userPermissions,
    isSMSizeDP,
  } = useSidebar();

  return (
    <Box sx={{ display: 'flex', position: 'relative' }}>
      <Drawer
        variant='permanent'
        open={isSidebarOpen}
        sx={{
          display: 'flex',
          width: '100%',
          '.MuiPaper-root': {
            position: 'relative',
            height: '100%',
          },
        }}
      >
        <List sx={{ padding: '8px' }}>
          <Divider sx={{ display: 'none', my: '12px' }} />
          {sidebarOptions.map(option => {
            const checkIsAdmin =
              option.path === '/admin' ? hasAdminPanelPermission : true;
            const hasPermissions = option.requiredPermissions
              ? option.requiredPermissions.some(item =>
                  userPermissions.includes(item),
                )
              : true;

            return (
              <Fragment key={option.label}>
                {checkIsAdmin && hasPermissions && (
                  <ListItem
                    disablePadding
                    sx={{
                      display: 'block',
                    }}
                  >
                    {option.hasDivider && <Divider sx={{ my: '12px' }} />}

                    <Link to={option.path}>
                      <ListItemButton
                        selected={isOptionFocused(option.path)}
                        sx={{
                          padding: isSidebarOpen ? '12px 16px' : '12px',
                          gap: '32px',
                          borderRight: isOptionFocused(option.path)
                            ? `2px solid ${theme.palette.primary.main}`
                            : 'unset',
                          [theme.breakpoints.down('md')]: {
                            padding: isSidebarOpen ? '8px 12px' : '12px',
                          },
                        }}
                      >
                        <Tooltip
                          title={t(option.label)}
                          placement='right'
                          disableFocusListener={isSidebarOpen}
                          disableHoverListener={isSidebarOpen}
                        >
                          {option.icon}
                        </Tooltip>
                        {isSidebarOpen && !isSMSizeDP && (
                          <Typography
                            variant='body1'
                            sx={{
                              fontWeight: 400,
                              minWidth: '142px',
                              color: 'text.primary',
                            }}
                          >
                            {t(option.label)}
                          </Typography>
                        )}
                      </ListItemButton>
                    </Link>
                  </ListItem>
                )}
              </Fragment>
            );
          })}
        </List>
        {!isSMSizeDP && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 12,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={handleDrawerToggle}
          >
            {isSidebarOpen ? (
              <>
                <Typography
                  variant='body2'
                  sx={{ color: theme.palette.primary.main }}
                >
                  {t('general.collapse')}
                </Typography>
                <IconButton>
                  <ArrowBack color='primary' />
                </IconButton>
              </>
            ) : (
              <Tooltip title={t('general.expand')} placement='right'>
                <IconButton>
                  <ArrowForward color='primary' />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
};
